import Axios from 'axios';

import { AUTH_COOKIE_ONLY, BASE_URL, HTTP_STATUS } from 'utils/const';

/**
 * Cookie で認証していると記録する localStorage のキー (AUTH_COOKIE_ONLY のときだけ使う)。
 * 値は「起動時に認証の要る API で確かめに行くかどうか」の目印にすぎず、ログイン中かどうかは
 * 必ず API の応答 (verifySession) で決める。トークンそのものは HttpOnly の Cookie にあり JS からは読めない
 */
export const COOKIE_SESSION_STORAGE_KEY = 'cookieSession';

/**
 * API (uzone_api) を呼ぶクライアント
 *
 * - withCredentials: API のホストの HttpOnly の Cookie (__Host-uzone_{env}_admin_token) を送り、受け取る
 * - X-Requested-With: Cookie で認証する状態変更のリクエストは、API がこのヘッダと Origin を確かめる
 *   (uzone_api の VerifyCookieAuthOrigin)。Bearer のときは見られないが、常に付けておく
 *
 * S3 の署名付き URL など API 以外へ送るときは使わないこと (Cookie やヘッダが CORS で拒まれる)。
 * 画像のアップロードは fetch で送っている (hooks/useImageUpload.js)
 */
const axios = Axios.create({
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
});

axios.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (
      error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
      !error.config?.skipUnauthorizedRedirect
    ) {
      localStorage.setItem('accessToken', '');
      localStorage.removeItem(COOKIE_SESSION_STORAGE_KEY);
      window.location.assign(window.location.origin);
    }

    return Promise.reject(error);
  },
);

let accessToken = '';

export const setApiToken = (token) => {
  accessToken = token;
};

/**
 * 認証のヘッダ
 *
 * AUTH_COOKIE_ONLY のときは Authorization を付けない (付けると API は Cookie を見ない)。
 * それ以外はこれまでどおり Bearer を付ける (Cookie も届くが、API は Authorization を先に使う)
 */
const authHeaders = () => (AUTH_COOKIE_ONLY ? {} : { Authorization: `Bearer ${accessToken}` });

export const getApi = async (url, params) => {
  const res = await axios.get(url, {
    // ここに「params」というキー名でセットし、クエリパラメータを指定する
    params,
    headers: authHeaders(),
  });

  return res;
};

/**
 * @param {object} [config] axios の設定に足すもの (skipUnauthorizedRedirect など)
 */
export const putApi = async (url, params, config = {}) => {
  const res = await axios.put(
    url,
    // ここに「params」というキー名でセットし、クエリパラメータを指定する
    params,
    {
      ...config,
      headers: authHeaders(),
    },
  );

  return res;
};

export const postApi = (url, params) => {
  const res = axios.post(url, params, {
    headers: authHeaders(),
  });

  return res;
};

export const deleteApi = async (url, params) => {
  const res = await axios.delete(url, {
    data: params,
    headers: authHeaders(),
  });

  return res;
};

/**
 * verifySession の結果
 *
 * - AUTHENTICATED: ログイン中 (2xx)
 * - UNAUTHENTICATED: ログインしていない (401・419)
 * - UNKNOWN: 決められない (通信エラー・タイムアウト・5xx・その他)。ログイン中かどうかは分からない
 */
export const SESSION_STATUS = Object.freeze({
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  UNKNOWN: 'unknown',
});

/**
 * ログイン中の管理者 (自分) を返す API (uzone_api #3074)
 *
 * 外部管理者も呼べる (restrict.external を掛けていない)。応答は
 * `{ data: { admin_id, admin_name, email, is_super_admin (boolean), account_status_code, account_status_name,
 * is_external_admin, external_admin_role, supplier_name, supplier_id } }`
 */
export const ACCOUNT_ME_URL = `${BASE_URL}/account/me`;

/**
 * verifySession が ACCOUNT_ME_URL で受け取った自分のアカウント (応答の data)。まだ誰も使っていなければ入っている
 *
 * Cookie だけで認証するときは、起動時・ログイン直後に verifySession が ACCOUNT_ME_URL を呼び、
 * そのすぐあとに MainLayout が fetchMyAccount で同じ API を呼んでいた (1 回の表示で 2 回)。
 * verifySession の応答をここに残し、fetchMyAccount が API を呼ばずに一度だけ使う。
 */
let prefetchedMyAccount = null;

/**
 * verifySession が受け取った自分のアカウントを取り出す (取り出したら消す。無ければ null)
 *
 * 一度だけ使えるようにしているのは、古い値を使い続けないため。以後は redux (administratorSlice) に残る
 */
export const takePrefetchedMyAccount = () => {
  const account = prefetchedMyAccount;
  prefetchedMyAccount = null;

  return account;
};

/**
 * Cookie のトークンでログイン中かを、認証の要る API (ACCOUNT_ME_URL) を呼んで確かめる (AUTH_COOKIE_ONLY のとき)
 *
 * 管理者には JS から読めるログイン中の目印の Cookie が無いので、API の応答で判断する。
 * - 2xx: AUTHENTICATED
 * - 401・419: UNAUTHENTICATED
 * - 通信エラー・タイムアウト・5xx・その他: UNKNOWN (一時的な障害でログアウトさせないよう、呼び出し側で区別する)
 *
 * 403 は特別扱いしない (UNKNOWN)。ACCOUNT_ME_URL は外部管理者にも 403 を返さない
 * (restrict.external を掛けていない)。呼び出し側 (lib/auth) は UNAUTHENTICATED かどうかだけを見るので、
 * もし 403 が返っても、ログイン中として扱うのは AUTHENTICATED のときと同じ。
 *
 * 2xx なら、応答の自分のアカウントを fetchMyAccount が使えるよう残す (takePrefetchedMyAccount)。
 *
 * 401 でもトップへ飛ばさない (起動時・ログイン直後に呼ぶので、呼び出し側で未ログインとして扱う)。
 *
 * @returns {Promise<'authenticated'|'unauthenticated'|'unknown'>} SESSION_STATUS のいずれか
 */
export const verifySession = async () => {
  prefetchedMyAccount = null;

  try {
    const res = await axios.get(ACCOUNT_ME_URL, {
      headers: authHeaders(),
      skipUnauthorizedRedirect: true,
    });
    prefetchedMyAccount = res?.data ?? null;

    return SESSION_STATUS.AUTHENTICATED;
  } catch (error) {
    const status = error.response?.status;
    if (status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.PAGE_EXPIRED) {
      return SESSION_STATUS.UNAUTHENTICATED;
    }

    return SESSION_STATUS.UNKNOWN;
  }
};

export default axios;
